import {
  ArrowLeftRight,
  LogOut,
  Monitor,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  Settings,
  Sun,
  User as UserIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown";
import { useQuery } from "@tanstack/react-query";

import { NotificationBell } from "@/components/layout/NotificationBell";
import { TenantBrandLogo } from "@/components/layout/TenantBrandLogo";
import { OdinoLauncher } from "@/components/odino/OdinoLauncher";
import { appsApi } from "@/lib/api-apps";
import { meApi } from "@/lib/api-me";
import { reportTypesApi } from "@/lib/api-reports";
import { useAuthStore } from "@/lib/auth";
import { getSupportVisit } from "@/lib/supportSession";
import { fetchTenantContext } from "@/lib/platform";
import { endSession } from "@/lib/session";
import { usePermissions } from "@/lib/permissions";
import { useTheme } from "@/theme/ThemeProvider";

/**
 * Path segment → i18n key for the breadcrumb. Unknown segments fall
 * back to the raw value (e.g. workflow UUIDs).
 */
const BREADCRUMB_KEYS: Record<string, string> = {
  dashboard: "sidebar.dashboard",
  messages: "sidebar.messages",
  mappings: "sidebar.mappings",
  canonical: "sidebar.canonical",
  credentials: "sidebar.credentials",
  users: "sidebar.users",
  audit: "sidebar.auditLog",
  apps: "sidebar.apps",
  sources: "sidebar.sources",
  datasets: "sidebar.datasets",
  semantics: "semantics.title",
  graph: "relationships.graphTitle",
  pages: "apps.pages",
  "report-types": "sidebar.reports",
  workflows: "sidebar.workflows",
  "workflow-runs": "sidebar.workflowRuns",
  "aggregate-buckets": "sidebar.aggregateBuckets",
  webhooks: "sidebar.webhooks",
  profile: "profile.title",
  account: "profile.title",
  docs: "sidebar.docs",
};

export interface TopbarProps {
  /**
   * Called when the user clicks the hamburger. On mobile this opens
   * the off-canvas drawer; on desktop it toggles the sidebar
   * collapsed-rail state. The branching is owned by AppShell.
   */
  onMenuClick?: () => void;
  /** Used for the hamburger tooltip on desktop. */
  sidebarCollapsed?: boolean;
  /** Opens the global Cmd+K command palette. */
  onOpenSearch?: () => void;
  /** Toggles the docked Odino assistant rail (the launcher lives here in the
   *  header — no floating button). */
  onToggleOdino?: () => void;
  /** Whether the Odino rail is currently open (drives the button's active state). */
  odinoOpen?: boolean;
  /** Focus mode suppresses the in-topbar launcher — AppShell renders an
   *  interactive twin outside the inert/blurred chrome instead. */
  hideOdinoLauncher?: boolean;
}

export function Topbar({
  onMenuClick,
  sidebarCollapsed,
  onOpenSearch,
  onToggleOdino,
  odinoOpen,
  hideOdinoLauncher,
}: TopbarProps) {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = () => void endSession();
  const { canSee } = usePermissions();

  // The workspace SWITCHER (ADR-0077 S4): shown only to a CENTRAL member
  // (`me.identity_id`, shared ["me"] cache) on a host with a central door
  // (`central_login_url` — absent on-prem, so the item never renders there).
  // `?picker=1` forces the picker on `app.<base>` (no auto-handoff bounce-back).
  const meQ = useQuery({ queryKey: ["me"], queryFn: () => meApi.get(), staleTime: 60_000 });
  const ctxQ = useQuery({
    queryKey: ["tenant-context"],
    queryFn: fetchTenantContext,
    staleTime: 300_000,
  });
  const switchUrl =
    meQ.data?.identity_id && ctxQ.data?.central_login_url
      ? `${ctxQ.data.central_login_url}?picker=1`
      : null;

  const segments = location.pathname.split("/").filter(Boolean);

  // Resolve UUID segments in the apps area (#7): `.../apps/<id>/pages/<id>`
  // should read as the app name + page title, not raw UUIDs. The app/pages
  // queries reuse the keys the apps pages use, so they're cache hits.
  const isUuid = (s: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
  const appsIdx = segments.indexOf("apps");
  const appId = appsIdx >= 0 && isUuid(segments[appsIdx + 1] ?? "") ? segments[appsIdx + 1] : null;
  const pagesIdx = segments.indexOf("pages");
  const pageId =
    pagesIdx >= 0 && isUuid(segments[pagesIdx + 1] ?? "") ? segments[pagesIdx + 1] : null;
  const appQ = useQuery({
    queryKey: ["apps", appId, "detail"],
    queryFn: () => appsApi.get(appId!),
    enabled: !!appId,
    staleTime: 60_000,
  });
  const pagesQ = useQuery({
    queryKey: ["apps", appId, "pages"],
    queryFn: () => appsApi.listPages(appId!),
    enabled: !!appId && !!pageId,
    staleTime: 60_000,
  });

  // Same resolution for the report designer: `/report-types/<id>/reports/<id>/designer`
  // reads as Report / <type name> / <report name>. The query keys mirror
  // ReportDesignerPage's, so they're cache hits.
  const rtIdx = segments.indexOf("report-types");
  const reportTypeId = rtIdx >= 0 && isUuid(segments[rtIdx + 1] ?? "") ? segments[rtIdx + 1] : null;
  const reportId =
    reportTypeId && segments[rtIdx + 2] === "reports" && isUuid(segments[rtIdx + 3] ?? "")
      ? segments[rtIdx + 3]
      : null;
  const reportTypeQ = useQuery({
    queryKey: ["report-types", reportTypeId],
    queryFn: () => reportTypesApi.get(reportTypeId!),
    enabled: !!reportTypeId,
    staleTime: 60_000,
  });
  const reportsQ = useQuery({
    queryKey: ["report-types", reportTypeId, "reports"],
    queryFn: () => reportTypesApi.listReports(reportTypeId!),
    enabled: !!reportTypeId && !!reportId,
    staleTime: 60_000,
  });

  const labelFor = (seg: string): string => {
    if (seg === appId) return appQ.data?.name ?? seg;
    if (seg === pageId) return pagesQ.data?.find((p) => p.id === seg)?.title ?? seg;
    if (seg === reportTypeId) return reportTypeQ.data?.name ?? seg;
    if (seg === reportId) return reportsQ.data?.find((r) => r.id === seg)?.name ?? seg;
    return BREADCRUMB_KEYS[seg] ? t(BREADCRUMB_KEYS[seg]) : seg;
  };
  // The GLOBAL breadcrumb stops at the APP in the page builder — the page is
  // selected inside the builder's own header dropdown, so detailing
  // `…/pages/<id>` here is noise (owner). Truncate at the `pages` segment;
  // every other route keeps its full path.
  let crumbs: { seg: string; to: string }[] = segments.map((seg, i) => ({
    seg,
    to: `/${segments.slice(0, i + 1).join("/")}`,
  }));
  if (appId && pagesIdx >= 0) crumbs = crumbs.slice(0, pagesIdx);
  // The report designer's intermediate paths (`/report-types/<id>`, `…/reports`)
  // have no routes of their own (the catch-all would bounce to "/"), so the
  // breadcrumb collapses to Report / <type> / <report> — the type crumb links
  // back to the list, the report name is the terminal crumb.
  if (reportTypeId && reportId) {
    crumbs = [
      { seg: "report-types", to: "/report-types" },
      { seg: reportTypeId, to: "/report-types" },
      { seg: reportId, to: location.pathname },
    ];
  }
  const lastSegment = crumbs[crumbs.length - 1]?.seg;
  const lastLabel = lastSegment ? labelFor(lastSegment) : "";

  return (
    <header className="relative grid h-topbar grid-cols-[1fr_auto_1fr] items-center bg-surface px-3 sm:px-5 min-w-0 gap-2 after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-gradient-to-r after:from-transparent after:via-border after:to-transparent">
      {/* LEFT zone — menu + breadcrumb. */}
      <div className="flex min-w-0 items-center gap-2 justify-self-start">
        {/* Sidebar toggle — always visible. The icon mirrors the
            current state so the action ("expand" vs "collapse") is
            obvious before clicking:
              - sidebar visible    → PanelLeftClose (click hides it)
              - sidebar collapsed  → PanelLeftOpen  (click shows it)
            On mobile the rail is hidden by default so we always show
            PanelLeftOpen there (the drawer slides in on click). */}
        <Button
          variant="ghost"
          size="icon"
          className="-ml-1"
          aria-label={sidebarCollapsed ? t("sidebar.expandSidebar") : t("sidebar.collapseSidebar")}
          title={sidebarCollapsed ? t("sidebar.expandSidebar") : t("sidebar.collapseSidebar")}
          onClick={onMenuClick}
        >
          {/* On mobile: always "open" affordance — the rail isn't
              visible regardless of `sidebarCollapsed`. */}
          <PanelLeftOpen className="h-4 w-4 md:hidden" strokeWidth={1.5} />
          {/* On desktop: reflect actual state. */}
          {sidebarCollapsed ? (
            <PanelLeftOpen className="hidden md:block h-4 w-4" strokeWidth={1.5} />
          ) : (
            <PanelLeftClose className="hidden md:block h-4 w-4" strokeWidth={1.5} />
          )}
        </Button>

        {/* Compact breadcrumb on mobile: only the last (current) segment. */}
        <div className="md:hidden min-w-0 truncate text-sm font-medium text-foreground">
          {lastLabel}
        </div>

        {/* Full breadcrumb on >=md. Every segment but the last (the current
            page) links to its cumulative path, so e.g. "App" jumps to the
            apps list and an app name jumps to that app's detail. */}
        <nav className="hidden md:flex items-center gap-1.5 text-sm min-w-0">
          {crumbs.map((crumb, i) => {
            const label = labelFor(crumb.seg);
            const isLast = i === crumbs.length - 1;
            return (
              <span key={i} className="flex items-center gap-1.5">
                {i > 0 && <span className="text-muted-foreground/50">/</span>}
                {isLast ? (
                  <span className="text-foreground font-medium">{label}</span>
                ) : (
                  <Link
                    to={crumb.to}
                    className="rounded text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {label}
                  </Link>
                )}
              </span>
            );
          })}
        </nav>
      </div>

      {/* CENTER zone — the search sits in its OWN geometrically-centred cell
          (the two 1fr grid columns are equal), so it never gets pulled into the
          right-hand action cluster (the "ammucchiato" the owner reported).
          Desktop-only: on mobile the grid centre column is empty and ⌘K + the
          icon actions carry search. */}
      <div className="hidden justify-self-center md:block">
        {onOpenSearch && (
          <button
            type="button"
            onClick={onOpenSearch}
            aria-label={t("topbar.search", "Search…")}
            title={t("topbar.search", "Search…")}
            className="inline-flex h-9 w-[22rem] max-w-[40vw] items-center gap-2 rounded-md border border-border bg-background px-3 text-sm text-muted-foreground transition hover:border-muted-foreground hover:text-foreground"
          >
            <Search className="h-4 w-4" strokeWidth={1.5} />
            <span className="flex-1 text-left">{t("topbar.search", "Search…")}</span>
            <kbd className="rounded border border-border bg-surface px-1.5 py-0.5 font-mono text-[11px]">
              ⌘K
            </kbd>
          </button>
        )}
      </div>

      {/* RIGHT zone — the action cluster only (Odino · notifiche · theme · account). */}
      <div className="flex items-center gap-3 shrink-0 justify-self-end">
        {/* Odino assistant launcher — lives in the header (no floating button,
            which could cover page content). Toggles the docked right rail; the
            active state mirrors whether the rail is open. Suppressed in focus
            mode, where AppShell renders a crisp twin OUTSIDE the inert chrome
            (the topbar lock would otherwise trap it). */}
        {onToggleOdino && !hideOdinoLauncher && (
          <OdinoLauncher open={odinoOpen} onToggle={onToggleOdino} />
        )}

        {/* Notification bell — the live-stream health is folded INTO it now
            (a status pip that appears only when degraded), so there's no
            standalone dot cluttering the cluster. */}
        {canSee("notifications") && <NotificationBell />}

        {/* Hairline divider so the notifications cluster reads apart from the
            theme/account group. Order: notifiche · | · theme · account. */}
        <span className="hidden md:block h-5 w-px bg-border" />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Theme">
              {theme === "dark" ? (
                <Moon className="h-4 w-4" strokeWidth={1.5} />
              ) : theme === "light" ? (
                <Sun className="h-4 w-4" strokeWidth={1.5} />
              ) : (
                <Monitor className="h-4 w-4" strokeWidth={1.5} />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setTheme("light")}>
              <Sun className="h-4 w-4" strokeWidth={1.5} /> {t("topbar.theme.light")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme("dark")}>
              <Moon className="h-4 w-4" strokeWidth={1.5} /> {t("topbar.theme.dark")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme("system")}>
              <Monitor className="h-4 w-4" strokeWidth={1.5} /> {t("topbar.theme.system")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-2">
              <UserIcon className="h-4 w-4" strokeWidth={1.5} />
              <span className="hidden sm:inline">{user?.username ?? "—"}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {/* Tenant logo (ADR-0036 Fase 2) — moved off the header action rail
                into the account panel, where the brand identifies the workspace
                without competing with the controls. Renders nothing if unset. */}
            <TenantBrandLogo className="mx-3 mt-2 mb-1 h-6 max-w-[8rem]" />
            <div className="px-3 py-2">
              <div className="text-sm font-medium">{user?.full_name ?? user?.username ?? "—"}</div>
              <div className="text-xs text-muted-foreground">{user?.email}</div>
              {user?.groups && user.groups.length > 0 && (
                <div className="mt-1 text-xs text-muted-foreground">
                  {t("sidebar.users")}: {user.groups.join(", ")}
                </div>
              )}
            </div>
            <DropdownMenuSeparator />
            {switchUrl && (
              <DropdownMenuItem
                onClick={() => {
                  // Identity-changing navigation (another subdomain) — a hard
                  // location change, never an SPA route.
                  window.location.href = switchUrl;
                }}
              >
                <ArrowLeftRight className="h-4 w-4" strokeWidth={1.5} />{" "}
                {t("topbar.switchWorkspace", "Cambia workspace")}
              </DropdownMenuItem>
            )}
            {/* Hidden during a support visit: a visitor is accountless (ADR-0081 D6), so
                there is no profile of theirs to open — and the page would only offer a
                password and personal API keys that cannot and must not exist for them.
                The backend refuses the whole surface too; this just stops offering it. */}
            {!getSupportVisit() && (
              <DropdownMenuItem onClick={() => navigate("/profile")}>
                <Settings className="h-4 w-4" strokeWidth={1.5} /> {t("topbar.profile")}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() => {
                logout();
                navigate("/login");
              }}
            >
              <LogOut className="h-4 w-4" strokeWidth={1.5} /> {t("topbar.logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
